import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Alert,
  Typography,
  Tabs,
  Card,
  Table,
  Button,
  Space,
  Transfer,
  Select,
  Input,
  Row,
  Col,
  Tag,
  message,
  Popconfirm,
  Tooltip,
  Checkbox,
  Divider,
  Statistic,
  Progress,
  Badge,
} from "antd";
import {
  TeamOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  SearchOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import type { TransferDirection } from "antd/es/transfer";
import { usePayrollProcessing } from "../store/context";
import { PayrollPeriod, HREmployee, PayrollEmployee } from "../../../types/hr";
import { formatCurrency } from "../utils";
import { useAuth } from "../../../context/AuthContext";
import { useSelection } from "../../../SelectionContext";
import useBusinessResource from "../../../hooks/useBusinessResource";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateBusinessResources } from "../../../utils/businessResourceFreshness";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { Search } = Input;

interface EmployeeManagementModalProps {
  visible: boolean;
  period: PayrollPeriod | null;
  onCancel: () => void;
  onSuccess: () => void;
}

interface AvailableEmployee extends Pick<HREmployee, 'id' | 'first_name' | 'last_name' | 'employee_number' | 'status'> {
  department_id?: number;
  position_id?: number;
  department_name?: string;
  position_title?: string;
  key: string;
  title: string;
  description: string;
  chosen: boolean;
}

interface EmployeeFilters {
  department?: number;
  position?: number;
  status?: string;
  search?: string;
}

const EmployeeManagementModal: React.FC<EmployeeManagementModalProps> = ({
  visible,
  period,
  onCancel,
  onSuccess,
}) => {
  const {
    addEmployeesToPeriod,
    removeEmployeeFromPeriod,
  } = usePayrollProcessing();

  // États locaux
  const [loading, setLoading] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState<
    AvailableEmployee[]
  >([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [filters, setFilters] = useState<EmployeeFilters>({});
  const [transferData, setTransferData] = useState<AvailableEmployee[]>([]);
  const [targetKeys, setTargetKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [activeTab, setActiveTab] = useState("current");

  const { user, token } = useAuth();
  const { selectedCompany } = useSelection();
  const queryClient = useQueryClient();
  const scope = { userId: user?.id, companyId: period?.company_id, warehouseId: "company", moduleScope: "hr" };
  const [periodEmployees, setPeriodEmployees] = useState<PayrollEmployee[]>([]);
  const [dataError, setDataError] = useState<string | null>(null);
  const sourceSignature = useRef<string | null>(null);
  const selectionDirty = useRef(false);
  const currentKey = useRef("");
  currentKey.current = visible && period ? `${period.id}:${period.company_id}:${user?.id}:${selectedCompany}` : "";
  const resource = useBusinessResource({
    ...scope, resource: "payroll-roster", parameters: [period?.id], active: visible,
    enabled: Boolean(visible && token && period && Number(selectedCompany) === Number(period.company_id)),
    load: async signal => {
      if (!period) throw new Error("Période manquante");
      const read = async (url: string) => {
        const response = await fetch(url, { signal, headers: { Authorization: `Bearer ${token}`, "X-Company-Id": String(period.company_id) } });
        const result = await response.json();
        if (!response.ok || !result.success || !Array.isArray(result.data)) throw new Error(result.message || result.error || "Lecture des employés impossible");
        return result.data;
      };
      const [employees, available, departments, positions] = await Promise.all([
        read(`/api/hr/payroll-processing/periods/${period.id}/employees`), read("/api/hr/employees"),
        read("/api/hr/departments"), read("/api/hr/positions"),
      ]);
      return { employees: employees as PayrollEmployee[], available: available as HREmployee[], departments, positions };
    },
  });
  const isLoading = resource.loading;
  const canMutate = resource.status === "verified" && !dataError && !loading;

  useEffect(() => {
    sourceSignature.current = null; selectionDirty.current = false;
    setPeriodEmployees([]); setAvailableEmployees([]); setTransferData([]); setTargetKeys([]); setSelectedKeys([]); setDataError(null);
  }, [visible, period?.id, period?.company_id, user?.id, selectedCompany]);

  useEffect(() => {
    if (!visible || !period) return;
    if (Number(selectedCompany) !== Number(period.company_id)) {
      setDataError("L’entreprise sélectionnée a changé. Rouvrez la gestion des employés."); return;
    }
    if (resource.error) {
      setDataError(resource.error instanceof Error ? resource.error.message : "Lecture des employés impossible"); return;
    }
    if (resource.status !== "verified" || !resource.data) return;
    const data = resource.data;
    const signature = JSON.stringify(data);
    if (selectionDirty.current && sourceSignature.current !== signature) {
      setDataError("La liste des employés a changé pendant la sélection. Votre sélection est conservée. Actualisez avant d’appliquer les modifications."); return;
    }
    const ids = new Set(data.employees.map(employee => Number(employee.employee_id)));
    const transformed = data.available.map(employee => ({ ...employee, key: String(employee.id),
      title: `${employee.first_name} ${employee.last_name}`,
      description: `${employee.employee_number} - ${employee.department_name || "Aucun département"} - ${employee.position_title || "Aucun poste"}`,
      chosen: ids.has(Number(employee.id)),
    }));
    // Preserve roster entries which disappear from the available-employees list;
    // omission from a choice API must never request their removal implicitly.
    const choices = [...transformed, ...data.employees.filter(employee => !transformed.some(choice => Number(choice.id) === Number(employee.employee_id)))
      .map(employee => ({ ...employee, id: employee.employee_id, key: String(employee.employee_id), title: `${employee.first_name} ${employee.last_name}`,
        description: employee.employee_number, chosen: true }))];
    setPeriodEmployees(data.employees); setAvailableEmployees(transformed); setTransferData(choices);
    if (!selectionDirty.current) setTargetKeys(data.employees.map(employee => String(employee.employee_id)));
    setDepartments(data.departments); setPositions(data.positions); setDataError(null); sourceSignature.current = signature;
  }, [visible, period?.id, selectedCompany, resource.data, resource.status, resource.error]);

  const loadData = async () => {
    selectionDirty.current = false;
    try { await resource.refresh({ force: true }); }
    catch (error) { setDataError(error instanceof Error ? error.message : "Lecture des employés impossible"); throw error; }
  };
  const checkLatestSelection = async () => {
    if (!canMutate) throw new Error(dataError || "Actualisez la liste des employés avant toute modification");
    const expected = sourceSignature.current;
    const data = await resource.refresh({ force: true });
    if (JSON.stringify(data) !== expected) throw new Error("La liste des employés a changé. Votre sélection est conservée ; actualisez avant de poursuivre.");
  };
  const refreshAfterMutation = async () => {
    selectionDirty.current = false;
    await invalidateBusinessResources(queryClient, scope, "payroll-employees");
    await resource.refresh({ force: true });
  };

  const handleTransferChange = (
    newTargetKeys: React.Key[],
    direction: TransferDirection,
    moveKeys: React.Key[]
  ) => {
    selectionDirty.current = true;
    setTargetKeys(newTargetKeys);
  };

  const handleTransferSelectChange = (
    sourceSelectedKeys: React.Key[],
    targetSelectedKeys: React.Key[]
  ) => {
    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
  };

  const handleApplyChanges = async () => {
    if (!period?.id) return;

    try {
      await checkLatestSelection();
      const key = currentKey.current;
      setLoading(true);

      // Employés actuellement assignés
      const currentEmployeeIds = periodEmployees.map((pe) =>
        pe.employee_id.toString()
      );

      // Employés à ajouter (dans targetKeys mais pas dans current)
      const employeesToAdd = targetKeys
        .map((key) => key.toString())
        .filter((key) => !currentEmployeeIds.includes(key));

      // Employés à supprimer (dans current mais pas dans targetKeys)
      const employeesToRemove = currentEmployeeIds.filter(
        (id) => !targetKeys.map((k) => k.toString()).includes(id)
      );

      // Ajouter les nouveaux employés
      if (employeesToAdd.length > 0) {
        await addEmployeesToPeriod(
          period.id,
          employeesToAdd.map((id) => parseInt(id))
        );
      }

      // Supprimer les employés non sélectionnés
      for (const employeeId of employeesToRemove) {
        await removeEmployeeFromPeriod(period.id, parseInt(employeeId));
      }

      if (currentKey.current !== key) return;
      await refreshAfterMutation();
      message.success("Modifications appliquées avec succès");
      onSuccess();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Erreur lors de l'application des modifications");
      console.error("Error applying changes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAllActive = async () => {
    if (!period?.id) return;

    try {
      await checkLatestSelection();
      setLoading(true);

      const response = await fetch(`/api/hr/payroll-processing/periods/${period.id}/employees`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'X-Company-Id': String(period.company_id) },
        body: JSON.stringify({ include_all: true }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || result.error || "Ajout des salariés impossible");

      // Recharger les données
      await refreshAfterMutation();
      message.success(result.message || "Salariés de la période ajoutés");
      onSuccess();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Erreur lors de l'ajout des employés");
      console.error("Error adding all employees:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveEmployee = async (employeeId: number) => {
    if (!period?.id) return;

    try {
      await checkLatestSelection();
      setLoading(true);
      await removeEmployeeFromPeriod(period.id, employeeId);
      message.success("Employé retiré de la période");

      // Recharger les données
      await refreshAfterMutation();
      onSuccess();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Erreur lors de la suppression");
      console.error("Error removing employee:", error);
    } finally { setLoading(false); }
  };

  const getEmployeeProgress = () => {
    const total = periodEmployees.length;
    const calculated = periodEmployees.filter(
      (emp) => emp.is_calculated
    ).length;
    const validated = periodEmployees.filter((emp) => emp.is_validated).length;
    const withErrors = periodEmployees.filter(
      (emp) => emp.calculation_errors && emp.calculation_errors.length > 0
    ).length;

    return { total, calculated, validated, withErrors };
  };

  const filterTransferData = (employees: AvailableEmployee[]) => {
    return employees.filter((emp) => {
      // Filtre par département
      if (filters.department && emp.department_id !== filters.department) {
        return false;
      }

      // Filtre par poste
      if (filters.position && emp.position_id !== filters.position) {
        return false;
      }

      // Filtre par statut
      if (filters.status && emp.status !== filters.status) {
        return false;
      }

      // Filtre par recherche
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        return (
          emp.first_name.toLowerCase().includes(searchLower) ||
          emp.last_name.toLowerCase().includes(searchLower) ||
          emp.employee_number.toLowerCase().includes(searchLower) ||
          emp.department_name?.toLowerCase().includes(searchLower) ||
          emp.position_title?.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  };

  const currentEmployeeColumns = [
    {
      title: "Employé",
      key: "employee",
      render: (_: any, record: PayrollEmployee) => (
        <div>
          <Text strong>
            {record.first_name} {record.last_name}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.employee_number}
          </Text>
        </div>
      ),
    },
    {
      title: "Département",
      dataIndex: "department_name",
      key: "department",
      render: (dept: string) => dept || "-",
    },
    {
      title: "Poste",
      dataIndex: "position_name",
      key: "position",
      render: (pos: string) => pos || "-",
    },
    {
      title: "Salaire de Base",
      dataIndex: "base_salary",
      key: "salary",
      render: (salary: number) => (salary ? formatCurrency(salary) : "-"),
    },
    {
      title: "Statut",
      key: "status",
      render: (_: any, record: PayrollEmployee) => (
        <Space direction="vertical" size="small">
          <Tag color={record.has_variables ? "green" : "orange"}>
            {record.has_variables
              ? "Variables saisies"
              : "Variables manquantes"}
          </Tag>
          <Tag color={record.is_calculated ? "blue" : "default"}>
            {record.is_calculated ? "Calculé" : "Non calculé"}
          </Tag>
          {record.is_validated && <Tag color="success">Validé</Tag>}
          {record.calculation_errors &&
            record.calculation_errors.length > 0 && (
              <Tag color="error">Erreurs</Tag>
            )}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: PayrollEmployee) => (
        <Popconfirm
          title="Retirer cet employé de la période ?"
          description="Cette action supprimera également ses variables et calculs."
          onConfirm={() => handleRemoveEmployee(record.employee_id)}
          okText="Confirmer"
          cancelText="Annuler"
        >
          <Button type="text" danger icon={<UserDeleteOutlined />} size="small" disabled={!canMutate}>
            Retirer
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const filteredTransferData = filterTransferData(transferData);
  const progress = getEmployeeProgress();

  if (!period) return null;

  return (
    <Modal
      title={
        <Space>
          <TeamOutlined style={{ color: "#1890ff" }} />
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Gestion des Employés
            </Title>
            <Text type="secondary">{period.name}</Text>
          </div>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1200}
      footer={null}
      style={{ top: 20 }}
    >
      {/* Statistiques rapides */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Total Employés"
              value={progress.total}
              prefix={<TeamOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Calculés"
              value={progress.calculated}
              suffix={`/ ${progress.total}`}
              valueStyle={{
                color:
                  progress.calculated === progress.total
                    ? "#3f8600"
                    : "#faad14",
              }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Validés"
              value={progress.validated}
              suffix={`/ ${progress.total}`}
              valueStyle={{
                color:
                  progress.validated === progress.total ? "#3f8600" : "#1890ff",
              }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Erreurs"
              value={progress.withErrors}
              valueStyle={{
                color: progress.withErrors > 0 ? "#cf1322" : "#3f8600",
              }}
            />
          </Col>
        </Row>

        {progress.total > 0 && (
          <div style={{ marginTop: 16 }}>
            <Text>Progression des calculs:</Text>
            <Progress
              percent={Math.round((progress.calculated / progress.total) * 100)}
              status={
                progress.withErrors > 0
                  ? "exception"
                  : progress.calculated === progress.total
                  ? "success"
                  : "active"
              }
              format={(percent) => `${progress.calculated}/${progress.total}`}
            />
          </div>
        )}
      </Card>

      {dataError && <Alert type="error" showIcon message="Gestion des employés bloquée" description={dataError}
        action={<Button onClick={() => { void loadData().catch(() => {}); }}>Actualiser</Button>} style={{ marginBottom: 16 }} />}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* Onglet des employés actuels */}
        <TabPane
          tab={
            <span>
              <TeamOutlined />
              Employés Assignés{" "}
              {periodEmployees.length > 0 && (
                <Badge count={periodEmployees.length} />
              )}
            </span>
          }
          key="current"
        >
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => { void loadData().catch(() => {}); }}
                loading={loading}
              >
                Actualiser
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setActiveTab("add")}
              >
                Ajouter des Employés
              </Button>
            </Space>
          </div>

          <Table
            columns={currentEmployeeColumns}
            dataSource={periodEmployees}
            loading={isLoading}
            rowKey="employee_id"
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} employés`,
            }}
          />
        </TabPane>

        {/* Onglet d'ajout d'employés */}
        <TabPane
          tab={
            <span>
              <UserAddOutlined />
              Ajouter des Employés
            </span>
          }
          key="add"
        >
          {/* Filtres */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Search
                  placeholder="Rechercher un employé..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  allowClear
                />
              </Col>
              <Col span={6}>
                <Select
                  placeholder="Filtrer par département"
                  value={filters.department}
                  onChange={(value) =>
                    setFilters({ ...filters, department: value })
                  }
                  allowClear
                  style={{ width: "100%" }}
                >
                  {departments.map((dept) => (
                    <Option key={dept.id} value={dept.id}>
                      {dept.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <Select
                  placeholder="Filtrer par poste"
                  value={filters.position}
                  onChange={(value) =>
                    setFilters({ ...filters, position: value })
                  }
                  allowClear
                  style={{ width: "100%" }}
                >
                  {positions.map((pos) => (
                    <Option key={pos.id} value={pos.id}>
                      {pos.title}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <Select
                  placeholder="Filtrer par statut"
                  value={filters.status}
                  onChange={(value) =>
                    setFilters({ ...filters, status: value })
                  }
                  allowClear
                  style={{ width: "100%" }}
                >
                  <Option value="active">Actif</Option>
                  <Option value="inactive">Inactif</Option>
                </Select>
              </Col>
            </Row>
          </Card>

          {/* Actions rapides */}
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleApplyChanges}
                loading={loading}
                disabled={!canMutate || transferData.length === 0}
              >
                Appliquer les Modifications
              </Button>
              <Button
                icon={<TeamOutlined />}
                onClick={handleAddAllActive}
                loading={loading}
                disabled={!canMutate}
              >
                Ajouter les Salariés de la Période
              </Button>
            </Space>
          </div>

          {/* Transfer Component */}
          <Transfer
            disabled={!canMutate}
            dataSource={filteredTransferData}
            titles={["Employés Disponibles", "Employés Sélectionnés"]}
            targetKeys={targetKeys}
            selectedKeys={selectedKeys}
            onChange={handleTransferChange}
            onSelectChange={handleTransferSelectChange}
            render={(item) => (
              <div>
                <div>{item.title}</div>
                <div style={{ color: "#666", fontSize: "12px" }}>
                  {item.description}
                </div>
              </div>
            )}
            listStyle={{
              width: 400,
              height: 400,
            }}
            showSearch
            filterOption={(search, item) =>
              item.title.toLowerCase().includes(search.toLowerCase()) ||
              item.description.toLowerCase().includes(search.toLowerCase())
            }
            locale={{
              itemUnit: "employé",
              itemsUnit: "employés",
              searchPlaceholder: "Rechercher...",
              notFoundContent: "Aucun employé trouvé",
            }}
          />

          <Alert
            message="Instructions"
            description="Utilisez les filtres pour restreindre la liste. Sélectionnez les employés à ajouter ou retirer de la période, puis cliquez sur 'Appliquer les Modifications'."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default EmployeeManagementModal;
